import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  RequirementListItem,
  RequirementDetailsResponse,
  RequirementStatus,
  Language,
} from '@shared/types';
import { RequirementCard } from '../../components/admin/RequirementCard';
import { RequirementDetails } from '../../components/admin/RequirementDetails';
import { MarkdownExport } from '../../components/admin/MarkdownExport';
import apiService from '../../services/api.service';
import { removeToken } from '../../services/auth.service';

/**
 * RequirementsPage Component
 * 
 * Admin page for viewing and managing synthesized requirements.
 * 
 * Requirements:
 * - US4: Requirements list and detail view
 * - FR-014: Structured requirement display
 * - FR-015: Markdown export
 */

export const RequirementsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // List view state
  const [requirements, setRequirements] = useState<RequirementListItem[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<RequirementStatus | 'ALL'>('ALL');
  const [languageFilter, setLanguageFilter] = useState<Language | 'ALL'>('ALL');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Detail view state
  const [selectedRequirement, setSelectedRequirement] =
    useState<RequirementDetailsResponse | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  /**
   * Load requirements list
   */
  const loadRequirements = async () => {
    try {
      setIsLoadingList(true);
      setListError(null);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
      });

      if (statusFilter !== 'ALL') {
        params.append('status', statusFilter);
      }
      if (languageFilter !== 'ALL') {
        params.append('language', languageFilter);
      }

      const response = await apiService.get<{
        requirements: RequirementListItem[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>(`/api/admin/requirements?${params.toString()}`);

      setRequirements(response.data.requirements);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error: any) {
      console.error('Failed to load requirements:', error);
      if (error.response?.status === 401) {
        removeToken();
        navigate('/admin/login');
      } else {
        setListError(t('requirement.list.errors.loadFailed'));
      }
    } finally {
      setIsLoadingList(false);
    }
  };

  /**
   * Load requirement details
   */
  const loadRequirementDetails = async (requirementId: string) => {
    try {
      setIsLoadingDetail(true);
      setDetailError(null);

      const response = await apiService.get<RequirementDetailsResponse>(
        `/api/admin/requirements/${requirementId}`
      );

      setSelectedRequirement(response.data);
    } catch (error: any) {
      console.error('Failed to load requirement details:', error);
      if (error.response?.status === 404) {
        setDetailError(t('requirement.detail.errors.notFound'));
      } else if (error.response?.status === 401) {
        removeToken();
        navigate('/admin/login');
      } else {
        setDetailError(t('requirement.detail.errors.loadFailed'));
      }
    } finally {
      setIsLoadingDetail(false);
    }
  };

  /**
   * Handle view details
   */
  const handleViewDetails = (requirementId: string) => {
    navigate(`/admin/requirements/${requirementId}`);
  };

  /**
   * Handle back to list
   */
  const handleBackToList = () => {
    setSelectedRequirement(null);
    navigate('/admin/requirements');
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    removeToken();
    navigate('/admin/login');
  };

  // Load list on mount and when filters change
  useEffect(() => {
    if (!id) {
      loadRequirements();
    }
  }, [currentPage, statusFilter, languageFilter, id]);

  // Load details when ID changes
  useEffect(() => {
    if (id) {
      loadRequirementDetails(id);
    } else {
      setSelectedRequirement(null);
    }
  }, [id]);

  // Detail view
  if (id && selectedRequirement) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <button
                  onClick={handleBackToList}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-2 transition-colors"
                >
                  ← {t('requirement.detail.backToList')}
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t('requirement.detail.title')}
                </h1>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {t('admin.dashboard.logout')}
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          {isLoadingDetail ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {t('requirement.detail.loading')}
              </p>
            </div>
          ) : detailError ? (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
              <button
                onClick={handleBackToList}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t('requirement.detail.backToList')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left column - Metadata */}
              <div className="space-y-6">
                {/* Status card */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('requirement.detail.metadata')}
                  </h2>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm text-gray-600 dark:text-gray-400">
                        {t('requirement.detail.status')}
                      </dt>
                      <dd className="mt-1">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            selectedRequirement.requirement.status === 'COMPLETED'
                              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                              : selectedRequirement.requirement.status === 'PROCESSING'
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                : selectedRequirement.requirement.status === 'FAILED'
                                  ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                  : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                          }`}
                        >
                          {t(`requirement.status.${selectedRequirement.requirement.status.toLowerCase()}`)}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600 dark:text-gray-400">
                        {t('requirement.detail.language')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {selectedRequirement.requirement.language === 'HU'
                          ? '🇭🇺 Magyar'
                          : '🇬🇧 English'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600 dark:text-gray-400">
                        {t('requirement.detail.created')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {new Date(selectedRequirement.requirement.createdAt).toLocaleString()}
                      </dd>
                    </div>
                    {selectedRequirement.requirement.processedAt && (
                      <div>
                        <dt className="text-sm text-gray-600 dark:text-gray-400">
                          {t('requirement.detail.processed')}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {new Date(
                            selectedRequirement.requirement.processedAt
                          ).toLocaleString()}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Visitor info */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('requirement.detail.visitorInfo')}
                  </h2>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-600 dark:text-gray-400">
                        {t('requirement.detail.name')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {selectedRequirement.visitor.name}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600 dark:text-gray-400">
                        {t('requirement.detail.email')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {selectedRequirement.visitor.email}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Export */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('requirement.detail.export')}
                  </h2>
                  <MarkdownExport
                    markdown={selectedRequirement.requirement.rawMarkdown}
                    filename={`requirement-${selectedRequirement.requirement.id}`}
                  />
                </div>
              </div>

              {/* Right column - Synthesized spec */}
              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                    {t('requirement.detail.synthesizedSpec')}
                  </h2>
                  {selectedRequirement.requirement.status === 'COMPLETED' &&
                  selectedRequirement.requirement.synthesizedSpec ? (
                    <RequirementDetails
                      synthesizedSpec={selectedRequirement.requirement.synthesizedSpec}
                    />
                  ) : selectedRequirement.requirement.status === 'FAILED' ? (
                    <div className="text-center py-8">
                      <svg
                        className="mx-auto h-12 w-12 text-red-400 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-gray-600 dark:text-gray-400 mb-2">
                        {t('requirement.detail.synthesisFailed')}
                      </p>
                      {selectedRequirement.requirement.errorMessage && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {selectedRequirement.requirement.errorMessage}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">
                        {t('requirement.detail.processing')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('requirement.list.title')}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('requirement.list.subtitle')}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {t('requirement.list.backToDashboard')}
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {t('admin.dashboard.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('requirement.list.filters.status')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as RequirementStatus | 'ALL');
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">{t('requirement.list.filters.all')}</option>
                <option value="PENDING">{t('requirement.status.pending')}</option>
                <option value="PROCESSING">{t('requirement.status.processing')}</option>
                <option value="COMPLETED">{t('requirement.status.completed')}</option>
                <option value="FAILED">{t('requirement.status.failed')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('requirement.list.filters.language')}
              </label>
              <select
                value={languageFilter}
                onChange={(e) => {
                  setLanguageFilter(e.target.value as Language | 'ALL');
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">{t('requirement.list.filters.all')}</option>
                <option value="HU">🇭🇺 Magyar</option>
                <option value="EN">🇬🇧 English</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoadingList ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('requirement.list.loading')}
            </p>
          </div>
        ) : listError ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">{listError}</p>
          </div>
        ) : requirements.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-gray-600 dark:text-gray-400">
              {t('requirement.list.noRequirements')}
            </p>
          </div>
        ) : (
          <>
            {/* Requirements grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {requirements.map((requirement) => (
                <RequirementCard
                  key={requirement.id}
                  requirement={requirement}
                  onClick={() => handleViewDetails(requirement.id)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-lg shadow-sm">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('requirement.list.pagination.previous')}
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('requirement.list.pagination.next')}
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {t('requirement.list.pagination.page')} <span className="font-medium">{currentPage}</span>{' '}
                      {t('requirement.list.pagination.of')} <span className="font-medium">{totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">{t('requirement.list.pagination.previous')}</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">{t('requirement.list.pagination.next')}</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};
